import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { Message, MessagePrev } from "@/types";
import { CustomTypography } from "../../custom-typography/custom-typography";
import { CustomMarkdown } from "../../custom-markdown/custom-markdown";
import { AuthenticatedImage } from "../../authenticated-media/authenticated-image";
import { formatDate } from "../../../helpers/dateUtils";
import {
  getAudioDisplayText,
  getVideoDisplayText,
} from "../../../helpers/messageHelpers";
import { useAppStore } from "../../../stores/appStore";
import { useInfoPanelListRowHelpers } from "../../../hooks/useInfoPanelListRowHelpers";
import {
  reactionsContainerStyles,
  getReactionButtonStyles,
  reactedMessageBoxStyles,
  markdownContainerStyles,
  reactedMessagesListStyles,
  reactedMessageFooterStyles,
  lessonBadgeStyles,
  imageCollectionBoxStyles,
  textWrapperStyles,
} from "./styles";

interface ReactionsPanelProps {
  markedIds: { [key: string]: string };
  activeReaction: string;
  setActiveReaction: (reaction: string) => void;
  reactedMessages: MessagePrev[];
  isLoading: boolean;
  error: string | null;
  onMessageClick: (messageId: string, domain: string, focus: string) => void;
  lessons?: MessagePrev[];
  messages?: Message[];
  crashCourses?: MessagePrev[];
}

export const ReactionsPanel: React.FC<ReactionsPanelProps> = ({
  markedIds,
  activeReaction,
  setActiveReaction,
  reactedMessages,
  isLoading,
  error,
  onMessageClick,
  lessons = [],
  messages = [],
  crashCourses = [],
}) => {
  const { domain: appDomain, focus: appFocus } = useAppStore();

  const reactedMessagesForContext = React.useMemo(
    () =>
      reactedMessages.filter(
        (m) => m.domain === appDomain && m.focus === appFocus,
      ),
    [reactedMessages, appDomain, appFocus],
  );

  const { getTabbedText, getBadgeLabel } = useInfoPanelListRowHelpers({
    panelRows: reactedMessagesForContext,
    messages,
    lessons,
    crashCourses,
  });

  const renderMessageContent = (msg: MessagePrev) => {
    if (msg.type === "image" && msg.file_url) {
      return (
        <AuthenticatedImage
          src={msg.file_url}
          alt="Image message"
          sx={{ width: "100%", borderRadius: 2, marginBottom: "5px" }}
        />
      );
    }
    if (
      (msg.type === "image_collection" ||
        msg.type === "flash_card_image_collection") &&
      msg.file_url
    ) {
      return (
        <Box sx={imageCollectionBoxStyles}>
          <AuthenticatedImage
            src={msg.file_url}
            alt="Image collection messsage"
            sx={{
              maxWidth: "40%",
              flexShrink: 0,
              borderRadius: 2,
              marginBottom: "5px",
            }}
          />
          <Box sx={textWrapperStyles}>
            <CustomMarkdown>{msg.text}</CustomMarkdown>
          </Box>
        </Box>
      );
    }
    if (msg.type === "tabbed_message" || msg.tabbed_content?.length) {
      return (
        <Box sx={markdownContainerStyles}>
          <CustomMarkdown>{getTabbedText(msg)}</CustomMarkdown>
        </Box>
      );
    }
    return (
      <Box sx={markdownContainerStyles}>
        <CustomMarkdown>
          {msg.type === "audio"
            ? getAudioDisplayText(msg)
            : msg.type === "video"
              ? getVideoDisplayText(msg)
              : msg.text}
        </CustomMarkdown>
      </Box>
    );
  };

  return (
    <Box>
      <Box sx={reactionsContainerStyles}>
        {Object.entries(markedIds).map(([key, label]) => (
          <Button
            key={key}
            onClick={() => setActiveReaction(key)}
            sx={getReactionButtonStyles(activeReaction === key)}
          >
            {label}
          </Button>
        ))}
      </Box>
      {isLoading ? (
        <Typography>Loading...</Typography>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : reactedMessagesForContext.length === 0 ? (
        <Typography>
          No messages found for reaction{" "}
          {markedIds[activeReaction]
            ? markedIds[activeReaction]
            : activeReaction}{" "}
          in this focus
        </Typography>
      ) : (
        <Box sx={reactedMessagesListStyles}>
          {reactedMessagesForContext.map((msg) => (
            <Box
              key={msg.message_id}
              onClick={() =>
                onMessageClick(msg.message_id, msg.domain, msg.focus)
              }
              sx={reactedMessageBoxStyles}
            >
              {renderMessageContent(msg)}
              <Box sx={reactedMessageFooterStyles}>
                <CustomTypography variant="caption" color="text.secondary">
                  {formatDate(msg.date)}
                </CustomTypography>
                <Typography sx={lessonBadgeStyles}>{getBadgeLabel(msg)}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
